/**
 * Checkbox Component
 * 
 * A reusable checkbox component with various states and styles.
 * Includes support for labels, descriptions, and error messages.
 */
import { forwardRef, InputHTMLAttributes } from 'react'
import { cn } from '../../utils/tailwind-utils'

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /**
   * Label text for the checkbox
   */
  label?: string
  
  /**
   * Description text to display below the checkbox
   */
  description?: string
  
  /**
   * Error message to display
   */
  error?: string
  
  /**
   * Size variant of the checkbox
   * @default "md"
   */
  size?: 'sm' | 'md' | 'lg'
  
  /**
   * Additional CSS classes for the wrapper
   */
  wrapperClassName?: string
}

/**
 * Checkbox component for boolean input with various states
 * 
 * @example
 * // Basic usage
 * <Checkbox label="Accept terms and conditions" />
 * 
 * @example
 * // With description and error
 * <Checkbox 
 *   label="Subscribe to newsletter"
 *   description="Receive weekly updates about new products and features"
 *   error="This field is required"
 *   checked={isSubscribed}
 *   onChange={(e) => setIsSubscribed(e.target.checked)}
 * />
 * 
 * @example
 * // Small size
 * <Checkbox 
 *   label="Remember me"
 *   size="sm"
 * />
 */
const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      className,
      wrapperClassName,
      label,
      description,
      error,
      size = 'md',
      disabled,
      ...props
    },
    ref
  ) => {
    // Size classes mapping
    const sizeClasses = {
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-6 h-6',
    }
    
    // Label size classes mapping
    const labelSizeClasses = {
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
    }
    
    // Determine if there's an error
    const hasError = !!error
    
    return (
      <div className={cn('flex', wrapperClassName)}>
        <div className="flex h-fit items-center">
          <input
            type="checkbox"
            className={cn(
              'form-checkbox rounded border-border bg-transparent text-primary focus:ring-primary focus:ring-offset-background',
              sizeClasses[size],
              hasError && 'border-destructive focus:ring-destructive',
              disabled && 'opacity-50 cursor-not-allowed',
              className
            )}
            disabled={disabled}
            ref={ref}
            {...props}
          />
        </div>
        
        {(label || description || error) && (
          <div className="ml-2">
            {label && (
              <label 
                htmlFor={props.id} 
                className={cn(
                  'font-medium',
                  labelSizeClasses[size],
                  hasError ? 'text-destructive' : 'text-foreground',
                  disabled && 'opacity-50 cursor-not-allowed'
                )}
              >
                {label}
              </label>
            )}
            
            {description && (
              <p className="text-sm text-muted-foreground mt-1">
                {description}
              </p>
            )}
            
            {error && (
              <p className="text-sm text-destructive mt-1">
                {error}
              </p>
            )}
          </div>
        )}
      </div>
    )
  }
)

// Display name for debugging
Checkbox.displayName = 'Checkbox'

export default Checkbox
